import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input, Label, Textarea } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast, Toaster } from "sonner";
import { Plus, X, FileText, Trash2 } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { useAuth } from "../contexts/AuthContext";

const statusVariant = {
  draft: "neutral",
  in_progress: "info",
  submitted: "success",
};

export default function AssessmentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ framework_id: "", name: "", scope: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [as, fws] = await Promise.all([
        api.get("/compliance/assessments"),
        api.get("/compliance/frameworks"),
      ]);
      setItems(as.data.items || []);
      setFrameworks(fws.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/compliance/assessments", form);
      setShowForm(false);
      setForm({ framework_id: "", name: "", scope: "" });
      toast.success("Assessment created");
      navigate(`/assessments/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete assessment "${a.name}"?`)) return;
    try {
      await api.delete(`/compliance/assessments/${a.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const canDelete = user && ["admin", "compliance_officer"].includes(user.role);

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Self-Assessments</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Interactive framework questionnaires with draft saving, evidence attachment and compliance scoring.
          </p>
        </div>
        <Button data-testid="assessment-new" onClick={() => { setForm({ framework_id: "", name: "", scope: "" }); setShowForm(true); }}>
          <Plus className="w-4 h-4" /> New assessment
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="border-t border-b border-border">
                  <th className="text-left py-2.5 px-6 label-eyebrow">Assessment</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Framework</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Status</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Compliance</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Responses</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Updated</th>
                  <th className="text-right py-2.5 px-6 label-eyebrow">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">
                    <FileText className="w-8 h-8 mx-auto mb-2" />
                    No assessments yet. Create your first one.
                  </td></tr>
                )}
                {items.map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`assessment-row-${a.id}`}
                    className="border-b border-border hover:bg-muted/40 transition-colors cursor-pointer"
                    onClick={() => navigate(`/assessments/${a.id}`)}
                  >
                    <td className="py-3 px-6 font-medium max-w-md">
                      <div className="truncate">{a.name}</div>
                      {a.scope && <div className="text-xs text-muted-foreground truncate">{a.scope}</div>}
                    </td>
                    <td className="py-3 px-6"><Badge variant="primary">{a.framework_code}</Badge></td>
                    <td className="py-3 px-6">
                      <Badge variant={statusVariant[a.status] || "neutral"}>
                        {a.status.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="py-3 px-6">
                      <div className="flex items-center gap-2 min-w-[100px]">
                        <div className="h-1.5 bg-muted rounded-full flex-1 overflow-hidden">
                          <div className="h-full bg-primary" style={{ width: `${a.compliance_percentage}%` }} />
                        </div>
                        <span className="tabular-nums text-xs w-8 text-right">{a.compliance_percentage}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-6 tabular-nums text-muted-foreground">{a.response_count}</td>
                    <td className="py-3 px-6 text-muted-foreground text-xs">
                      {a.updated_at && formatDistanceToNow(new Date(a.updated_at), { addSuffix: true })}
                    </td>
                    <td className="py-3 px-6 text-right">
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="destructive"
                          data-testid={`assessment-delete-${a.id}`}
                          onClick={(e) => { e.stopPropagation(); del(a); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-lg card-surface animate-fade-in" onClick={(e) => e.stopPropagation()} data-testid="assessment-form-modal">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-base font-semibold">New assessment</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={create} className="p-6 space-y-4">
              <div>
                <Label>Framework</Label>
                <select
                  data-testid="assessment-form-framework"
                  value={form.framework_id}
                  onChange={(e) => setForm({ ...form, framework_id: e.target.value })}
                  required
                  className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm mt-1.5"
                >
                  <option value="">Select framework…</option>
                  {frameworks.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Name</Label>
                <Input
                  data-testid="assessment-form-name"
                  className="mt-1.5"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. ISO 27001 Q2 self-assessment"
                  required
                />
              </div>
              <div>
                <Label>Scope</Label>
                <Textarea
                  className="mt-1.5"
                  value={form.scope}
                  onChange={(e) => setForm({ ...form, scope: e.target.value })}
                  placeholder="Business units and systems in scope"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" data-testid="assessment-form-submit">Create</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
